import { useCallback, useEffect, useRef } from "react";

type RowEligibility = {
  readonly isEligible: boolean;
  /** Lower = closer to viewport center. */
  readonly priority: number;
};

type UseDiscoverRowVisibilityOptions = {
  readonly reportRowEligibility: (blockKey: string, eligibility: RowEligibility) => void;
};

type RowElement = HTMLElement;

const ROOT_MARGIN = "0px 0px 100% 0px";

const getRowPriority = (element: RowElement): number => {
  const { top, height } = element.getBoundingClientRect();
  const rowCenter = top + height / 2;
  const viewportCenter = window.innerHeight / 2;
  return Math.abs(rowCenter - viewportCenter);
};

// Group parameters into a single options object to reduce parameter count to 5
type UseIntersectionObserverParams = {
  refObserver: React.RefObject<IntersectionObserver | null>;
  refElements: React.RefObject<Map<string, RowElement>>;
  refIntersecting: React.RefObject<Map<string, boolean>>;
  refReportRowEligibility: React.RefObject<UseDiscoverRowVisibilityOptions["reportRowEligibility"]>;
  refRafId: React.RefObject<number | null>;
  reReportAll: () => void;
};

function useIntersectionObserver({
  refObserver,
  refElements,
  refIntersecting,
  refReportRowEligibility,
  refRafId,
  reReportAll,
}: UseIntersectionObserverParams) {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const blockKey = entry.target.getAttribute("data-discover-row-key");
          if (!blockKey) continue;
          // Stale notification: element was unobserved / replaced after the
          // browser already queued this callback.
          if (refElements.current.get(blockKey) !== entry.target) continue;

          // Remember the browser's own answer so the scroll path can reuse it.
          refIntersecting.current.set(blockKey, entry.isIntersecting);

          refReportRowEligibility.current(blockKey, {
            isEligible: entry.isIntersecting,
            priority: getRowPriority(entry.target as RowElement),
          });
        }
      },
      {
        root: null,
        rootMargin: ROOT_MARGIN,
        threshold: 0,
      },
    );

    refObserver.current = observer;
    for (const element of refElements.current.values()) {
      observer.observe(element);
    }

    // threshold: 0 only fires when a row enters/leaves the expanded root.
    // Refresh priorities on scroll/resize so queued order stays correct
    // while rows remain inside that window.
    const scheduleReReport = () => {
      if (refRafId.current != null) return;
      refRafId.current = requestAnimationFrame(() => {
        refRafId.current = null;
        reReportAll();
      });
    };

    window.addEventListener("scroll", scheduleReReport, { passive: true });
    window.addEventListener("resize", scheduleReReport, { passive: true });

    return () => {
      observer.disconnect();
      refObserver.current = null;
      window.removeEventListener("scroll", scheduleReReport);
      window.removeEventListener("resize", scheduleReReport);
      if (refRafId.current != null) {
        cancelAnimationFrame(refRafId.current);
        refRafId.current = null;
      }
    };
  }, [reReportAll, refElements, refIntersecting, refObserver, refReportRowEligibility, refRafId]);
}

/**
 * Observes all Discovery row shells with a single page-level
 * IntersectionObserver and reports their viewport eligibility/priority
 * to the shared scheduler through the controller API.
 *
 * - Eligibility always comes from the IntersectionObserver.
 * - Priority is refreshed on scroll/resize (rAF-throttled) so rows that stay
 *   inside the expanded root do not keep a stale distance-to-center.
 * - When a row shell leaves the DOM, it is reported as ineligible so the
 *   scheduler can return a queued row to idle.
 *
 * The hook owns visibility detection only: it does not load content,
 * schedule or cancel requests, or manage row state.
 *
 * `reportRowEligibility` is expected to be referentially stable.
 */
const useDiscoverRowVisibility = ({ reportRowEligibility }: UseDiscoverRowVisibilityOptions) => {
  const refObserver = useRef<IntersectionObserver | null>(null);
  const refElements = useRef(new Map<string, RowElement>());
  const refIntersecting = useRef(new Map<string, boolean>());
  const refReportRowEligibility = useRef(reportRowEligibility);
  const refCallbacks = useRef(new Map<string, (element: RowElement | null) => void>());
  const refRafId = useRef<number | null>(null);

  refReportRowEligibility.current = reportRowEligibility;

  // Re-reports priority only. Eligibility is the observer's last known answer.
  const reReportAll = useCallback(() => {
    for (const [blockKey, element] of refElements.current) {
      const isEligible = refIntersecting.current.get(blockKey);
      if (isEligible === undefined) continue; // observer hasn't reported yet

      refReportRowEligibility.current(blockKey, {
        isEligible,
        priority: getRowPriority(element),
      });
    }
  }, []);

  const registerRow = useCallback((blockKey: string, element: RowElement | null) => {
    const observer = refObserver.current;
    const previousElement = refElements.current.get(blockKey);

    if (previousElement === element) return;

    if (previousElement) {
      observer?.unobserve(previousElement);
      refElements.current.delete(blockKey);
      refIntersecting.current.delete(blockKey);
    }

    if (!element) {
      // The shell left the DOM. The observer won't report this, so do it
      // explicitly, otherwise the scheduler keeps a queued row queued.
      refReportRowEligibility.current(blockKey, {
        isEligible: false,
        priority: Number.POSITIVE_INFINITY,
      });
      return;
    }

    refElements.current.set(blockKey, element);
    observer?.observe(element);
  }, []);

  const getRowRef = useCallback(
    (blockKey: string) => {
      const existingCallback = refCallbacks.current.get(blockKey);
      if (existingCallback) return existingCallback;

      const callback = (element: RowElement | null) => {
        registerRow(blockKey, element);
      };
      refCallbacks.current.set(blockKey, callback);
      return callback;
    },
    [registerRow],
  );

  useIntersectionObserver({
    refObserver,
    refElements,
    refIntersecting,
    refReportRowEligibility,
    refRafId,
    reReportAll,
  });

  return getRowRef;
};

export default useDiscoverRowVisibility;
